// =============================================================================
// timetable-data.js – Data transformation for the Timetable tab
// =============================================================================
// Pure functions (no DOM). Extracted from timetable.js.
// =============================================================================

import { firstTime, getScheduleKey, getAppDate } from "./utils.js";
import { resolveFavoriteStop } from "./live-logic";

/**
 * Get the visible stops for a timetable display.
 * Resolves user settings → config defaults → fallback.
 */
export function getVisibleStops(state, cfg, lineId, scheduleKey, direction, fallback) {
  const fromSettings =
    state.settings?.timetableStops?.[lineId]?.[scheduleKey] ||
    state.settings?.timetableStops?.[lineId]?.[direction];
  if (fromSettings?.length) return fromSettings;
  const defaults =
    cfg.stopProfiles?.[lineId]?.timetableStops?.[scheduleKey] ||
    cfg.stopProfiles?.[lineId]?.timetableStops?.[direction] ||
    cfg.displayStopsOverrides?.[lineId]?.[scheduleKey] ||
    cfg.displayStopsOverrides?.[lineId]?.[direction] ||
    fallback;
  // If the user has a favorite stop that differs from the first column,
  // substitute it so the timetable reflects their chosen departure point.
  const userFav = resolveFavoriteStop(lineId, direction, state, cfg);
  const focusCity = state.settings?.focusCity || cfg.defaults?.focusCity || "BT";
  const cfgFav = cfg.focusCities?.[focusCity]?.favoriteStops?.[lineId]?.[direction] || cfg.favoriteStops?.[lineId]?.[direction];
  if (userFav && userFav !== cfgFav && defaults?.length > 0) {
    const result = [...defaults];
    if (cfgFav && result.includes(cfgFav) && !result.includes(userFav)) {
      result[result.indexOf(cfgFav)] = userFav;
    } else if (!result.includes(userFav)) {
      result[0] = userFav;
    }
    return result;
  }
  return defaults;
}

/**
 * Get the reference stops for sorting trips (first visible or favorite).
 */
export function getReferenceStops(state, cfg, lineId, direction, visibleStops) {
  const profile = cfg.stopProfiles?.[lineId] || {};
  const preferred = resolveFavoriteStop(lineId, direction, state, cfg);
  const directionCandidates =
    direction === "return"
      ? [
          state.settings?.returnInterchanges?.[lineId],
          ...(profile.returnInterchanges || []),
          preferred
        ]
      : [preferred, ...(profile.outboundHomeStops || [])];
  return [
    ...directionCandidates.filter((code) => visibleStops.includes(code)),
    ...visibleStops,
    ...directionCandidates
  ].filter(Boolean);
}

/**
 * Get the reference time for a trip (first available time from reference stops).
 */
export function getReferenceTime(trip, referenceStops) {
  for (const code of referenceStops) {
    const minutes = trip.stops?.[code];
    if (minutes !== undefined && minutes !== null) return minutes;
  }
  return firstTime(trip);
}

/**
 * Merge all stops from all trips while preserving their logical order.
 */
export function getAllStopsOrdered(trips) {
  if (!trips.length) return [];
  const allStops = [];
  trips.forEach((trip) => {
    const tripStops = Object.keys(trip.stops || {});
    let lastInsertedIdx = -1;
    tripStops.forEach((code) => {
      const existingIdx = allStops.indexOf(code);
      if (existingIdx === -1) {
        allStops.splice(lastInsertedIdx + 1, 0, code);
        lastInsertedIdx++;
      } else {
        lastInsertedIdx = existingIdx;
      }
    });
  });
  return allStops;
}

/**
 * Realign a list of stop codes to the route order of `refStops`.
 * Codes present in `refStops` are sorted by their index in it (stable); codes
 * NOT in `refStops` (e.g. orphaned codes after a GTFS refresh) are appended at
 * the end in their original relative order. Used by the Tabella edit-mode to
 * avoid new columns always landing at the end of the table.
 */
export function realignStopsToRoute(stops, refStops) {
  if (!Array.isArray(stops) || !Array.isArray(refStops) || refStops.length === 0) {
    return Array.isArray(stops) ? [...stops] : [];
  }
  // {{ stopCode: index }} for O(1) lookup; first occurrence wins (matches
  // the first-seen semantics of getAllStopsOrdered).
  const idxMap = new Map();
  refStops.forEach((code, i) => {
    if (!idxMap.has(code)) idxMap.set(code, i);
  });
  const withIdx = stops.map((code, originalIdx) => ({
    code,
    idx: idxMap.has(code) ? idxMap.get(code) : Number.POSITIVE_INFINITY,
    originalIdx
  }));
  withIdx.sort((a, b) =>
    a.idx !== b.idx ? a.idx - b.idx : a.originalIdx - b.originalIdx
  );
  return withIdx.map((x) => x.code);
}

/**
 * Format a feed validity date (YYYY-MM-DD) to a readable Italian string.
 */
export function formatFeedDate(dateStr) {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch (_e) {
    return dateStr;
  }
}
/**
 * Next departures from the user's selected boarding stop (Da).
 *
 * Pure helper backing the "Prossime corse" strip: picks upcoming trips at
 * `stopId` (>= fromMin), ordered by time, capped at `limit`.
 *
 * @param {Object} lineData   LineDataMap
 * @param {Object} opts
 * @param {string} opts.lineId     active line
 * @param {string} opts.stopId     boarding stop code (the "Da" stop)
 * @param {string} opts.dayType    "weekday" | "saturday" | "sunday"
 * @param {string} opts.direction  "outbound" | "inbound"
 * @param {number} [opts.fromMin]  current minute; defaults to now
 * @param {number} [opts.limit]    max entries, default 3
 * @returns {Array<{tripId: string, depMin: number}>}
 */
export function getNextDepartures(lineData, { lineId, stopId, dayType, direction, fromMin, limit = 3 }) {
  const key = getScheduleKey(lineId, dayType, direction);
  const trips = lineData?.[lineId]?.[key] || [];
  const now = getAppDate();
  const ref = fromMin ?? (now.getHours() * 60 + now.getMinutes());
  return trips
    .map((t) => ({ tripId: String(t.tripId ?? ""), depMin: t.stops?.[stopId] }))
    .filter((e) => e.depMin !== undefined && e.depMin !== null && e.depMin >= ref)
    .slice(0, limit);
}
